import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";
import "../css/privacy.scss";

function Privacy() {
  return (
    <main className="policy-page policy-page--privacy">
      <header className="policy-hero">
        <div>
          <p className="policy-eyebrow">Information · {siteConfig.brandName}</p>
          <h1>Privacy notice</h1>
        </div>
        <div className="policy-hero__intro">
          <p>
            A plain-language description of data visible in this demonstration
            storefront and what a production privacy notice would need to cover.
          </p>
          <span>Last updated 13 August 2026</span>
        </div>
      </header>

      <div className="policy-shell">
        <aside className="policy-sidebar">
          <nav aria-label="Customer policies">
            <p>Customer policies</p>
            <Link to="/shipping">Shipping &amp; delivery</Link>
            <Link to="/returns">Returns &amp; refunds</Link>
            <Link className="is-active" to="/privacy" aria-current="page">
              Privacy
            </Link>
            <Link to="/terms">Terms</Link>
          </nav>
          <nav className="policy-toc" aria-label="On this page">
            <p>On this page</p>
            <a href="#privacy-demo">This demo</a>
            <a href="#privacy-information">Information</a>
            <a href="#privacy-use">How it is used</a>
            <a href="#privacy-storage">Browser storage</a>
            <a href="#privacy-rights">Your choices</a>
            <a href="#privacy-contact">Contact</a>
          </nav>
        </aside>

        <article className="policy-content">
          <div className="policy-demo-note" role="note">
            <strong>Demo storefront notice</strong>
            <p>
              This is an illustrative frontend, not a deployed commerce service or a
              substitute for a production privacy policy. It has no connected customer,
              order, payment, marketing or support database.
            </p>
          </div>

          <section id="privacy-demo">
            <p className="policy-section-number">01</p>
            <h2>What this demo actually does</h2>
            <p>
              Product browsing happens in the browser. Adding an item stores cart data
              in your browser’s local storage so the cart can persist on the same device.
              The cart contains product identifiers, selected options and quantities.
            </p>
            <p>
              The contact form validates entries locally and simulates a success state;
              it does not send or save the submitted fields. The checkout is a visual
              demonstration and should never be given real payment or personal details.
            </p>
          </section>

          <section id="privacy-information">
            <p className="policy-section-number">02</p>
            <h2>Information a production store may collect</h2>
            <p>A connected store would normally describe categories such as:</p>
            <div className="policy-definition-list">
              <div>
                <strong>Identity &amp; contact</strong>
                <p>Name, email, telephone number and delivery or billing address.</p>
              </div>
              <div>
                <strong>Order &amp; support</strong>
                <p>Items, delivery status, returns and messages sent to customer care.</p>
              </div>
              <div>
                <strong>Technical &amp; usage</strong>
                <p>Device, browser, IP address, site interactions and cookie choices.</p>
              </div>
              <div>
                <strong>Payment</strong>
                <p>
                  Transaction status and limited payment metadata from a regulated
                  provider; a merchant should not store full card security codes.
                </p>
              </div>
            </div>
          </section>

          <section id="privacy-use">
            <p className="policy-section-number">03</p>
            <h2>Purposes, legal bases &amp; sharing</h2>
            <p>
              A production notice should identify why information is used and the
              applicable legal basis—for example, fulfilling an order, meeting legal
              obligations, responding with consent, or pursuing a proportionate
              legitimate interest.
            </p>
            <p>
              Necessary information may be shared with contracted hosting, payment,
              analytics, email and delivery providers, as well as authorities when
              legally required. It should not be sold, and each provider should receive
              only what it needs for its stated service.
            </p>
          </section>

          <section id="privacy-storage">
            <p className="policy-section-number">04</p>
            <h2>Browser storage, cookies &amp; retention</h2>
            <p>
              This demo uses local storage for the shopping cart. You can remove that
              data by clearing the cart or clearing site data in your browser. Essential
              browser storage should be separated from optional analytics or advertising
              cookies in a production implementation.
            </p>
            <p>
              A live service should publish specific retention periods or criteria,
              delete information when it is no longer needed and explain international
              transfers and safeguards. This static notice does not assert that those
              production systems exist.
            </p>
          </section>

          <section id="privacy-rights">
            <p className="policy-section-number">05</p>
            <h2>Your choices &amp; rights</h2>
            <p>
              Depending on location and context, people may have rights to access,
              correct, delete, restrict, object to or receive a portable copy of personal
              information, and to withdraw consent without affecting earlier processing.
            </p>
            <p>
              Identity may need to be verified before a production request is fulfilled.
              People may also complain to the relevant data-protection authority. Exact
              rights and response periods depend on applicable law.
            </p>
          </section>

          <section id="privacy-contact">
            <p className="policy-section-number">06</p>
            <h2>Questions &amp; future changes</h2>
            <p>
              A live policy should name the legal data controller and full registered
              address before launch. This demo displays the brand contact configured for
              the storefront: {siteConfig.contact.address}.
            </p>
            <p>
              Material changes should be dated and communicated appropriately. For this
              prototype, privacy questions can be directed to {siteConfig.contact.email}.
            </p>
            <div className="policy-contact-box">
              <div>
                <span>Privacy question?</span>
                <strong>{siteConfig.contact.email}</strong>
              </div>
              <Link to="/contact">Open the contact page</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
